import React, { useState, useEffect } from "react";
import "./Cme.css";
import DynamicBanner from '../../include/DynamicBanner';
import api from "../../../../api";

const Cme = () => {
  const [cmeData, setCmeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Desktop state
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [activeYearIndex, setActiveYearIndex] = useState(0);

  // Mobile state
  const [mobileYear, setMobileYear] = useState("");

  // helper to coerce things into arrays
  const asArray = (val) => {
    if (Array.isArray(val)) return val;
    if (val === undefined || val === null) return [];
    // if it's a string that contains comma-separated headings, split it
    if (typeof val === "string") {
      // if string contains commas and likely multiple headings -> split, else single element array
      return val.includes(",") ? val.split(",").map((s) => s.trim()) : [val];
    }
    // if it's an object and has numeric keys or '0' style -> try to convert
    if (typeof val === "object") {
      // if object looks like {0: something, 1: something} convert by values sorted by keys
      const keys = Object.keys(val);
      const numericKeys = keys.every((k) => String(parseInt(k, 10)) === k);
      if (numericKeys) {
        return keys
          .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
          .map((k) => val[k]);
      }
      // otherwise return single object as single item array
      return [val];
    }
    // fallback
    return [val];
  };

  // normalize tab structure so table_heading and rows are arrays
  const normalizeTab = (tab) => {
    if (!tab) return tab;
    const normalized = { ...tab };
    normalized.table_heading = asArray(tab.table_heading);

    // rows: ensure it's an array of arrays
    // possible backend shapes: rows: [ [cell,cell], [..] ] OR rows: {0: {...},1:{...}} OR rows: "..."
    const rawRows = tab.rows;
    let rowsArr = asArray(rawRows);

    // If each row is not an array, but an object representing columns, convert to array of values
    rowsArr = rowsArr.map((row) => {
      if (Array.isArray(row)) return row;
      if (row === null || row === undefined) return [];
      if (typeof row === "object") {
        // if it's object with numeric keys -> sort and map
        const keys = Object.keys(row);
        const numericKeys = keys.every((k) => String(parseInt(k, 10)) === k);
        if (numericKeys) {
          return keys
            .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
            .map((k) => row[k]);
        }
        // if object where each value is a cell object -> take values
        return Object.values(row);
      }
      // if it's primitive (string/number), treat as single-cell row
      return [row];
    });

    normalized.rows = rowsArr;
    return normalized;
  };

  useEffect(() => {
    const fetchCmeData = async () => {
      try {
        const response = await api.get("/api/sitepages/about-us/cme");
        const data = response.data;

        // defensive: ensure categories/tabs arrays exist
        const categories = Array.isArray(data.categories) ? data.categories : asArray(data.categories);

        // normalize every tab in every category
        const normalizedCategories = categories.map((cat) => {
          const tabs = Array.isArray(cat.tabs) ? cat.tabs : asArray(cat.tabs);
          const normTabs = tabs.map((t) => normalizeTab(t));
          return { ...cat, tabs: normTabs };
        });

        const normalizedData = {
          ...data,
          categories: normalizedCategories,
        };

        setCmeData(normalizedData);

        // set default selections
        if (normalizedCategories.length > 0) {
          setActiveCategoryIndex(0);
          if (normalizedCategories[0].tabs && normalizedCategories[0].tabs.length > 0) {
            setActiveYearIndex(0);
            setMobileYear(normalizedCategories[0].tabs[0].year || "");
          }
        }
      } catch (err) {
        setError(err.message || "Failed to fetch");
      } finally {
        setLoading(false);
      }
    };

    fetchCmeData();
  }, []);

  // If category index changes ensure activeYearIndex and mobileYear are in sync
  useEffect(() => {
    if (!cmeData) return;
    const categories = cmeData.categories || [];
    const cat = categories[activeCategoryIndex];
    if (!cat) return;
    if (!Array.isArray(cat.tabs) || cat.tabs.length === 0) {
      setActiveYearIndex(0);
      setMobileYear("");
      return;
    }
    // clamp year index
    const newYearIndex = Math.min(activeYearIndex, cat.tabs.length - 1);
    setActiveYearIndex(newYearIndex);
    setMobileYear(cat.tabs[newYearIndex]?.year || cat.tabs[0]?.year || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategoryIndex, cmeData]);

  if (loading) return <p>Loading...</p>;
  if (error || !cmeData) return <p>Error: {error}</p>;

  const { banner_data, categories } = cmeData;
  const activeCategory = categories[activeCategoryIndex] || null;
  const activeTab =
    activeCategory && Array.isArray(activeCategory.tabs)
      ? activeCategory.tabs[activeYearIndex] || activeCategory.tabs[0]
      : null;

  const mobileTab =
    activeCategory && Array.isArray(activeCategory.tabs)
      ? activeCategory.tabs.find((x) => x.year === mobileYear) || activeCategory.tabs[0]
      : null;
      const desktopBannerImages = Array.isArray(banner_data?.images)
  ? banner_data.images
  : [];

const mobileBannerImages = Array.isArray(banner_data?.mobile_images)
  ? banner_data.mobile_images
  : [];


  if (!activeCategory || !activeTab || !mobileTab) {
    return null;
  }

  // 🔹 Helper to render a cell with content + link + pdf
  const renderCell = (cell) => {
    if (!cell) return null;

    // support cases where cell might be a primitive string/number or an object with content/link/pdf_path
    if (typeof cell === "string" || typeof cell === "number") {
      return <div className="cme-cell-wrapper">{String(cell)}</div>;
    }

    const content = cell.content ?? cell.text ?? "";
    const pdfUrl = cell.pdf_path ? `${process.env.REACT_APP_API_URL}/public${cell.pdf_path}` : null;
    const link = cell.link ?? null;

    return (
      <div className="cme-cell-wrapper">
        {pdfUrl ? (
          <div className="mt-1">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="cme-pdf-link">
              <i className="fa fa-file-pdf-o me-1" aria-hidden="true"></i>
              {content}
            </a>
          </div>
        ) : link ? (
          <a href={link} target="_blank" rel="noopener noreferrer">
            {content}
          </a>
        ) : (
          content
        )}
      </div>
    );
  };

  return (
    <>
<DynamicBanner
  title={banner_data.title}
  bgImage={desktopBannerImages}
  bgImageMobile={mobileBannerImages}
/>


      <section className="cme-section-page ">
        {/* ---------- DESKTOP VIEW ---------- */}
        <div className="container mt-4 d-none d-md-block">
          <ul className="nav nav-tabs d-flex justify-content-center mb-5">
            {categories.map((cat, catIndex) => (
              <li key={catIndex} className="nav-item">
                <button
                  className={`nav-link ${activeCategoryIndex === catIndex ? "active" : ""}`}
                  onClick={() => {
                    setActiveCategoryIndex(catIndex);
                    setActiveYearIndex(0);
                  }}
                >
                  {cat.title}
                </button>
              </li>
            ))}
          </ul>

          <div className="tab-content mt-3">
            <div className="container tab-container">
              <div className="cme-sidetab">
                <div className="nav flex-column nav-pills me-3">
                  {(activeCategory.tabs || []).map((tab, yearIndex) => (
                    <button
                      key={yearIndex}
                      className={`nav-link ${activeYearIndex === yearIndex ? "active" : ""}`}
                      onClick={() => setActiveYearIndex(yearIndex)}
                    >
                      {tab.year}
                    </button>
                  ))}
                </div>
              </div>

              <div className="side-content">
                <h4 className="mb-3">Events of {activeTab.year}</h4>

                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        {(activeTab.table_heading || []).map((head, i) => (
                          <th key={i}>{head}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(activeTab.rows || []).map((row, rindex) => (
                        <tr key={rindex}>
                          {(row || []).map((cell, cindex) => (
                            <td key={cindex}>{renderCell(cell)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ---------- MOBILE VIEW ---------- */}
        <div className="container mt-4 d-block d-sm-none">
          <ul className="nav nav-tabs cme-mobile-tabs d-flex justify-content-center mb-4">
            {categories.map((cat, i) => (
              <li className="nav-item" key={i}>
                <button
                  className={`nav-link ${activeCategoryIndex === i ? "active" : ""}`}
                  onClick={() => {
                    setActiveCategoryIndex(i);
                    setMobileYear(categories[i].tabs?.[0]?.year || "");
                  }}
                >
                  {cat.title}
                </button>
              </li>
            ))}
          </ul>

          <div className="year-selection-container">
            <label className="form-label fw-semibold">Select Year:</label>
            <select
              className="form-select year-dropdown"
              value={mobileYear}
              onChange={(e) => setMobileYear(e.target.value)}
            >
              {(activeCategory.tabs || []).map((tab, tabIndex) => (
                <option key={tabIndex} value={tab.year}>
                  {tab.year}
                </option>
              ))}
            </select>
          </div>

          <div className="events-content mt-3">
            <div className="table-container">
              <h4 className="mb-4">Events of {mobileTab.year}</h4>

              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      {(mobileTab.table_heading || []).map((head, index) => (
                        <th key={index}>{head}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(mobileTab.rows || []).map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {(row || []).map((cell, cellIndex) => (
                          <td key={cellIndex}>{renderCell(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Cme;
